import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCalendarAlt, FaCheck, FaPlus } from "react-icons/fa";
import PageTemplate from "../components/PageTemplate";
import Spinner from "../components/Spinner";
import Modal from "../components/Modal";
import ErrorPage from "../components/ErrorPage";
import http from "../api/apiClient";
import { useCan } from "../authz/useCan";

import OverviewTab from "./schoolCalendar/tabs/OverviewTab";
import YearPlannerTab from "./schoolCalendar/tabs/YearPlannerTab";
import SchoolYearSelector from "./schoolCalendar/components/SchoolYearSelector";

import CalendarSubscriptions from "./schoolCalendar/components/CalendarSubscriptions";
import TabSelector from "./schoolCalendar/components/TabSelector";
import SchoolYearBadge from "./schoolCalendar/components/SchoolYearBadge";

import EventFormModal from "./schoolCalendar/modals/EventFormModal";
import DeleteEventModal from "./schoolCalendar/modals/DeleteEventModal";
import OfficialHolidayModal from "./schoolCalendar/modals/OfficialHolidayModal";
import InfoModal from "./schoolCalendar/modals/InfoModal";

import { API_PATH } from "./schoolCalendar/constants";
import type { IconComponent, ModalState, PlanningTab, SchoolCalendarEvent, SchoolYear } from "./schoolCalendar/types";
import {
  buildPayloadFromEvent,
  classroomLabel,
  entityId,
  eventClassroomNames,
  eventClassroomUuids,
  eventId,
  eventScope,
  formatDateOnly,
  formatEventTime,
  getDatePart,
  isCurrentSchoolYear,
  normalizeModalError,
  schoolYearId,
  schoolYearLabel,
} from "./schoolCalendar/utils";

import { useCalendarSubscriptions } from "./schoolCalendar/hooks/useCalendarSubscriptions";
import { useOfficialHolidays } from "./schoolCalendar/hooks/useOfficialHolidays";
import { useSchoolCalendarData } from "./schoolCalendar/hooks/useSchoolCalendarData";
import { useSchoolCalendarForm } from "./schoolCalendar/hooks/useSchoolCalendarForm";
import { useToast } from "./schoolCalendar/hooks/useToast";

const CalendarAltIcon = FaCalendarAlt as IconComponent;
const CheckIcon = FaCheck as IconComponent;
const PlusIcon = FaPlus as IconComponent;

const SchoolCalendar = () => {
  const { t, i18n } = useTranslation();

  const canRead = useCan("school.calendar", "read");
  const canCreate = useCan("school.calendar", "create");
  const canUpdate = useCan("school.calendar", "update");
  const canDelete = useCan("school.calendar", "delete");
  const canManage = useCan("school.calendar", "manage");

  const canView = canRead || canManage;
  const canAdd = canCreate || canManage;
  const canEdit = canUpdate || canManage;
  const canRemove = canDelete || canManage;

  const [modal, setModal] = useState<ModalState>({
    show: false,
    type: "info",
    title: "",
    message: "",
  });
  const [deleteTarget, setDeleteTarget] = useState<SchoolCalendarEvent | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [schoolYearDropdownOpen, setSchoolYearDropdownOpen] = useState(false);
  const [activePlanningTab, setActivePlanningTab] = useState<PlanningTab>("overview");
  const [yearPlannerSelection, setYearPlannerSelection] = useState({
    start: "",
    end: "",
    dragging: false,
  });
  const schoolYearDropdownRef = useRef<HTMLDivElement | null>(null);

  const { toast, showToast } = useToast();

  const eventTypeLabels = useMemo(
    () => ({
      HOLIDAY: t("schoolCalendar.eventTypes.HOLIDAY", { defaultValue: "Holiday" }),
      STUDY_DAY: t("schoolCalendar.eventTypes.STUDY_DAY", { defaultValue: "Study day" }),
      DAY_OFF: t("schoolCalendar.eventTypes.DAY_OFF", { defaultValue: "Day off" }),
      SCHOOL_EVENT: t("schoolCalendar.eventTypes.SCHOOL_EVENT", { defaultValue: "School activity" }),
      CLASS_ACTIVITY: t("schoolCalendar.eventTypes.CLASS_ACTIVITY", { defaultValue: "Class activity" }),
      FIELD_TRIP: t("schoolCalendar.eventTypes.FIELD_TRIP", { defaultValue: "Field trip / excursion" }),
      SPORTS_DAY: t("schoolCalendar.eventTypes.SPORTS_DAY", { defaultValue: "Sports day" }),
      PARENT_INFORMATION: t("schoolCalendar.eventTypes.PARENT_INFORMATION", { defaultValue: "Parent information" }),
      PARENT_EVENING: t("schoolCalendar.eventTypes.PARENT_EVENING", { defaultValue: "Parent evening" }),
      OTHER: t("schoolCalendar.eventTypes.OTHER", { defaultValue: "Other" }),
    }),
    [t],
  );

  const scopeLabels = useMemo(
    () => ({
      SCHOOL: t("schoolCalendar.scopes.SCHOOL", { defaultValue: "School-wide" }),
      CLASSROOM: t("schoolCalendar.scopes.CLASSROOM", { defaultValue: "Classroom" }),
    }),
    [t],
  );

  const {
    sortedSchoolYears,
    selectedSchoolYearUuid,
    selectedSchoolYear,
    selectedSchoolYearApiLabel,
    schoolYearGroups,
    yearClassrooms,
    classroomLookup,
    eventsLoading,
    loading,
    error,
    filters,
    setFilters,
    filteredEvents,
    yearPlannerMonths,
    loadEvents,
    handleSchoolYearChange: handleSchoolYearChangeData,
  } = useSchoolCalendarData({ t, i18n, canView });

  const openSchoolYearWarning = () => {
    setModal({
      show: true,
      type: "warning",
      title: t("schoolCalendar.fields.schoolYear", { defaultValue: "School year" }),
      message: t("schoolCalendar.precondition", {
        defaultValue: "Select a school year before managing the planning.",
      }),
    });
  };

  const {
    calendarLink,
    classroomCalendarLink,
    selectedClassroomForIcal,
    setSelectedClassroomForIcal,
    copiedSchoolLink,
    setCopiedSchoolLink,
    copiedClassroomLink,
    setCopiedClassroomLink,
    subscriptionsOpen,
    setSubscriptionsOpen,
    copyCalendarLink,
    copyClassroomCalendarLink,
    ensureSelectedClassroomForIcal,
  } = useCalendarSubscriptions({
    selectedSchoolYearUuid,
    yearClassrooms,
    onCopyFailed: () =>
      setModal({
        show: true,
        type: "error",
        title: t("common.modal.error", { defaultValue: "Error" }),
        message: t("schoolCalendar.copyFailed", { defaultValue: "Copying failed." }),
      }),
  });

  const {
    formOpen,
    setFormOpen,
    editingEvent,
    form,
    setForm,
    formErrors,
    saving,
    setSaving,
    openCreateForm,
    openEditForm,
    handleSubmit,
    resetFormState,
  } = useSchoolCalendarForm({
    t,
    canAdd,
    canEdit,
    selectedSchoolYearUuid,
    loadEvents: () => loadEvents(),
    showToast,
    setModal,
  });

  const {
    officialHolidayModalOpen,
    setOfficialHolidayModalOpen,
    officialHolidays,
    selectedOfficialHolidayIds,
    officialHolidaysLoading,
    officialHolidaysSaving,
    officialHolidaysError,
    openOfficialHolidayModal,
    toggleOfficialHolidaySelection,
    saveOfficialHolidays,
  } = useOfficialHolidays({
    t,
    canAdd,
    selectedSchoolYearUuid,
    selectedSchoolYearApiLabel,
    loadEvents: () => loadEvents(),
    showToast,
    openSchoolYearWarning,
  });

  useEffect(() => {
    ensureSelectedClassroomForIcal();

    setFilters((current) => {
      if (
        !current.classroomUuid ||
        yearClassrooms.some(
          (classroom) => String(entityId(classroom)) === String(current.classroomUuid),
        )
      ) {
        return current;
      }

      return { ...current, classroomUuid: "" };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchoolYearUuid, yearClassrooms]);

  useEffect(() => {
    if (!schoolYearDropdownOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!schoolYearDropdownRef.current?.contains(event.target as Node)) {
        setSchoolYearDropdownOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSchoolYearDropdownOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [schoolYearDropdownOpen]);

  const handleSchoolYearChange = (value: string) => {
    resetFormState();
    setSchoolYearDropdownOpen(false);
    handleSchoolYearChangeData(value);
  };

  const renderSchoolYearOption = (year: SchoolYear, { archive = false } = {}) => {
    const id = String(entityId(year));
    const isSelected = String(selectedSchoolYearUuid) === id;
    const current = isCurrentSchoolYear(year);
    const label = schoolYearLabel(year);

    return (
      <button
        key={id || label}
        type="button"
        onClick={() => handleSchoolYearChange(id)}
        className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition ${
          archive
            ? "text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800"
            : "text-gray-800 hover:bg-indigo-50 dark:text-gray-100 dark:hover:bg-gray-800"
        } ${isSelected ? "bg-indigo-50 dark:bg-indigo-900/30" : ""}`}
      >
        <span className="flex min-w-0 items-center gap-2">
          {isSelected && <CheckIcon className="shrink-0 text-indigo-600" />}
          <span className="truncate font-medium">{label}</span>
        </span>

        <span className="flex shrink-0 items-center gap-2">
          {current && (
            <SchoolYearBadge>
              {t("schoolCalendar.schoolYear.current", { defaultValue: "Currently active" })}
            </SchoolYearBadge>
          )}
          {archive && (
            <SchoolYearBadge tone="archive">
              {t("schoolCalendar.schoolYear.archive", { defaultValue: "Archive" })}
            </SchoolYearBadge>
          )}
        </span>
      </button>
    );
  };

  const handleDelete = async () => {
    const id = eventId(deleteTarget);
    if (!id || !canRemove) return;

    setSaving(true);

    try {
      await http.delete(`${API_PATH}/${id}`);
      setDeleteTarget(null);
      await loadEvents();
      showToast(
        t("schoolCalendar.deleted", { defaultValue: "The planning item was deleted." }),
      );
    } catch (err) {
      const normalized = normalizeModalError(
        err,
        t,
        t("schoolCalendar.deleteFailed", { defaultValue: "Deleting failed." }),
      );
      setModal({ show: true, type: "error", title: normalized.title, message: normalized.message });
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (event: SchoolCalendarEvent) => {
    const id = eventId(event);
    if (!id || !canEdit) return;

    setSaving(true);

    try {
      await http.put(
        `${API_PATH}/${id}`,
        buildPayloadFromEvent(event, { published: !Boolean(event?.published) }),
      );
      await loadEvents();
      showToast(
        t("schoolCalendar.publishChanged", { defaultValue: "The publication status was changed." }),
      );
    } catch (err) {
      const normalized = normalizeModalError(
        err,
        t,
        t("schoolCalendar.publishFailed", {
          defaultValue: "Changing the publication status failed.",
        }),
      );
      setModal({ show: true, type: "error", title: normalized.title, message: normalized.message });
    } finally {
      setSaving(false);
    }
  };

  // Several items in the overview can be selected and published at once.
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
  useEffect(() => {
    const visible = new Set(filteredEvents.map((event: SchoolCalendarEvent) => String(eventId(event))));
    setSelectedEventIds((current) => {
      const kept = current.filter((id) => visible.has(id));
      return kept.length === current.length ? current : kept;
    });
  }, [filteredEvents]);

  const selectedEvents = (): SchoolCalendarEvent[] =>
    filteredEvents.filter((event: SchoolCalendarEvent) => selectedEventIds.includes(String(eventId(event))));

  /** Runs one request per selected item, reloads once and reports how many failed. */
  const runForSelected = async (
    events: SchoolCalendarEvent[],
    request: (event: SchoolCalendarEvent) => Promise<unknown>,
    messages: { done: string; failed: (failed: number, total: number) => string; failedTitle: string },
  ) => {
    setSaving(true);
    let failed = 0;
    for (const event of events) {
      try {
        await request(event);
      } catch {
        failed += 1;
      }
    }
    try {
      await loadEvents();
    } finally {
      setSaving(false);
    }
    setSelectedEventIds([]);
    if (failed > 0) {
      setModal({ show: true, type: "error", title: messages.failedTitle, message: messages.failed(failed, events.length) });
    } else {
      showToast(messages.done);
    }
  };

  /** Publishes or unpublishes the selection; items that already have that status are skipped. */
  const setSelectedPublished = async (published: boolean) => {
    if (!canEdit) return;
    const events = selectedEvents().filter((event) => Boolean(event?.published) !== published);
    if (events.length === 0) {
      setSelectedEventIds([]);
      return;
    }
    await runForSelected(
      events,
      (event) => http.put(`${API_PATH}/${eventId(event)}`, buildPayloadFromEvent(event, { published })),
      {
        done: published
          ? t("schoolCalendar.publishSelectedDone", { count: events.length, defaultValue: "{{count}} items published." })
          : t("schoolCalendar.unpublishSelectedDone", { count: events.length, defaultValue: "{{count}} items unpublished." }),
        failed: (failed, total) => t("schoolCalendar.selectionFailed", {
          failed,
          total,
          defaultValue: "{{failed}} of {{total}} items could not be changed. Please try again.",
        }),
        failedTitle: t("schoolCalendar.publishFailed", { defaultValue: "Changing the publication status failed." }),
      },
    );
  };

  const [confirmDeleteSelected, setConfirmDeleteSelected] = useState(false);
  const deleteSelected = async () => {
    setConfirmDeleteSelected(false);
    if (!canRemove) return;
    const events = selectedEvents();
    if (events.length === 0) return;
    await runForSelected(
      events,
      (event) => http.delete(`${API_PATH}/${eventId(event)}`),
      {
        done: t("schoolCalendar.deleteSelectedDone", { count: events.length, defaultValue: "{{count}} items deleted." }),
        failed: (failed, total) => t("schoolCalendar.selectionFailed", {
          failed,
          total,
          defaultValue: "{{failed}} of {{total}} items could not be changed. Please try again.",
        }),
        failedTitle: t("schoolCalendar.deleteFailed", { defaultValue: "Deleting failed." }),
      },
    );
  };

  const normalizeYearPlannerRange = (start: string, end: string) => {
    if (!start) return { start: "", end: "" };
    if (!end) return { start, end: start };
    return start <= end ? { start, end } : { start: end, end: start };
  };

  const isDateInYearPlannerSelection = (dateKey: string) => {
    if (!yearPlannerSelection.start) return false;
    const { start, end } = normalizeYearPlannerRange(
      yearPlannerSelection.start,
      yearPlannerSelection.end || yearPlannerSelection.start,
    );
    return start <= dateKey && dateKey <= end;
  };

  const startYearPlannerSelection = (
    dateKey: string,
    pointerEvent: React.PointerEvent<HTMLDivElement>,
  ) => {
    if (!canAdd) return;
    pointerEvent.preventDefault();
    setYearPlannerSelection({ start: dateKey, end: dateKey, dragging: true });
  };

  const updateYearPlannerSelection = (dateKey: string) => {
    setYearPlannerSelection((current) =>
      current.dragging ? { ...current, end: dateKey } : current,
    );
  };

  const finishYearPlannerSelection = (dateKey: string) => {
    setYearPlannerSelection((current) => {
      if (!current.dragging || !current.start) return current;
      const { start, end } = normalizeYearPlannerRange(
        current.start,
        dateKey || current.end || current.start,
      );
      window.setTimeout(() => openCreateForm(start, end), 0);
      return { start: "", end: "", dragging: false };
    });
  };

  const cancelYearPlannerSelection = () => {
    setYearPlannerSelection({ start: "", end: "", dragging: false });
  };

  if (loading) return <Spinner />;

  if (error) {
    return <ErrorPage status={error.status} message={error.message} onRetry={() => loadEvents()} />;
  }

  return (
    <PageTemplate title="">
      <div className="w-full min-w-0 space-y-5 overflow-visible">
        <section className="relative overflow-hidden rounded-[2rem] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-indigo-900/50 dark:from-indigo-950/55 dark:via-slate-950 dark:to-sky-950/45 sm:px-8 sm:py-8">
          <div className="pointer-events-none absolute right-8 top-1/2 hidden -translate-y-1/2 text-sky-300/40 dark:text-sky-600/25 lg:block">
            <CalendarAltIcon className="h-36 w-36" />
          </div>

          <div className="relative z-10 flex min-w-0 flex-col gap-5 lg:flex-row lg:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-5">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-indigo-200 to-sky-50 text-3xl text-indigo-700 shadow-lg dark:border-slate-900 dark:from-indigo-800 dark:to-sky-950 dark:text-indigo-100 sm:h-24 sm:w-24">
                <CalendarAltIcon aria-hidden="true" />
              </div>

              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                  {t("school.overview.title", { defaultValue: "School structure" })}
                </p>
                <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                  {t("schoolCalendar.title", { defaultValue: "School planning" })}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                  {t("schoolCalendar.description", {
                    defaultValue: "Manage holidays, study days, school activities and classroom planning.",
                  })}
                </p>
                {selectedSchoolYear ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="inline-flex rounded-full border border-indigo-200 bg-white/80 px-3 py-1 text-xs font-semibold text-indigo-700 dark:border-indigo-700 dark:bg-indigo-950/70 dark:text-indigo-100">
                      {schoolYearLabel(selectedSchoolYear)}
                    </span>
                    {eventsLoading ? (
                      <span className="inline-flex rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700 dark:border-sky-800 dark:bg-sky-950/60 dark:text-sky-100">
                        {t("common.refreshing", { defaultValue: "Refreshing" })}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            {canAdd && selectedSchoolYearUuid ? (
              <div className="shrink-0 lg:ml-auto lg:mr-40">
                <button
                  type="button"
                  onClick={() => openCreateForm()}
                  className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-white/90 px-5 py-3 text-sm font-bold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-300 hover:bg-white hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-indigo-700 dark:bg-indigo-950/80 dark:text-indigo-100 dark:hover:bg-indigo-900"
                >
                  <PlusIcon className="h-4 w-4" />
                  {t("schoolCalendar.add", { defaultValue: "Add planning item" })}
                </button>
              </div>
            ) : null}
          </div>
        </section>
        {toast && (
          <div className="fixed right-4 top-4 z-[70] max-w-sm rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-900 shadow-xl shadow-emerald-900/10 dark:border-emerald-800/60 dark:bg-emerald-950 dark:text-emerald-100">
            <div role="status" aria-live="polite">
              {toast.message}
            </div>
          </div>
        )}

        <SchoolYearSelector
          t={t}
          selectedSchoolYear={selectedSchoolYear}
          selectedSchoolYearUuid={selectedSchoolYearUuid}
          schoolYearLabel={schoolYearLabel}
          isCurrentSchoolYear={isCurrentSchoolYear}
          SchoolYearBadge={SchoolYearBadge}
          schoolYearDropdownRef={schoolYearDropdownRef}
          schoolYearDropdownOpen={schoolYearDropdownOpen}
          setSchoolYearDropdownOpen={setSchoolYearDropdownOpen}
          schoolYearGroups={schoolYearGroups}
          renderSchoolYearOption={renderSchoolYearOption}
          activePlanningTab={activePlanningTab}
          canAdd={canAdd}
          openOfficialHolidayModal={openOfficialHolidayModal}
          officialHolidaysLoading={officialHolidaysLoading}
        />

        <CalendarSubscriptions
          t={t}
          subscriptionsOpen={subscriptionsOpen}
          setSubscriptionsOpen={setSubscriptionsOpen}
          calendarLink={calendarLink}
          copyCalendarLink={copyCalendarLink}
          copiedSchoolLink={copiedSchoolLink}
          selectedClassroomForIcal={selectedClassroomForIcal}
          setSelectedClassroomForIcal={setSelectedClassroomForIcal}
          setCopiedClassroomLink={setCopiedClassroomLink}
          yearClassrooms={yearClassrooms}
          entityId={entityId}
          classroomLabel={classroomLabel}
          classroomCalendarLink={classroomCalendarLink}
          copyClassroomCalendarLink={copyClassroomCalendarLink}
          copiedClassroomLink={copiedClassroomLink}
        />

        {!selectedSchoolYearUuid ? (
          <section className="rounded-2xl border border-dashed border-indigo-200 bg-indigo-50 px-6 py-12 text-center text-indigo-900 dark:border-indigo-500/40 dark:bg-indigo-950/30 dark:text-indigo-100">
            <CalendarAltIcon className="mx-auto mb-3 text-3xl" />
            <p className="font-semibold">
              {t("schoolCalendar.precondition", {
                defaultValue: "Select a school year before managing the planning.",
              })}
            </p>
          </section>
        ) : (
          <>
            <TabSelector
              t={t}
              activePlanningTab={activePlanningTab}
              setActivePlanningTab={setActivePlanningTab}
            />

            {activePlanningTab === "overview" && (
              <OverviewTab
                t={t}
                i18n={i18n}
                filters={filters}
                setFilters={setFilters}
                scopeLabels={scopeLabels}
                yearClassrooms={yearClassrooms}
                entityId={entityId}
                classroomLabel={classroomLabel}
                eventTypeLabels={eventTypeLabels}
                eventsLoading={eventsLoading}
                filteredEvents={filteredEvents}
                canAdd={canAdd}
                openCreateForm={openCreateForm}
                eventId={eventId}
                eventScope={eventScope}
                eventClassroomNames={eventClassroomNames}
                classroomLookup={classroomLookup}
                sortedSchoolYears={sortedSchoolYears}
                schoolYearId={schoolYearId}
                selectedSchoolYear={selectedSchoolYear}
                getDatePart={getDatePart}
                formatDateOnly={formatDateOnly}
                formatEventTime={formatEventTime}
                canEdit={canEdit}
                openEditForm={openEditForm}
                togglePublished={togglePublished}
                selectedEventIds={selectedEventIds}
                setSelectedEventIds={setSelectedEventIds}
                setSelectedPublished={setSelectedPublished}
                requestDeleteSelected={() => setConfirmDeleteSelected(true)}
                saving={saving}
                canRemove={canRemove}
                setDeleteTarget={setDeleteTarget}
                expandedEventId={expandedEventId}
                setExpandedEventId={setExpandedEventId}
                schoolYearLabel={schoolYearLabel}
              />
            )}

            {activePlanningTab === "yearPlanner" && (
              <YearPlannerTab
                t={t}
                selectedSchoolYear={selectedSchoolYear}
                schoolYearLabel={schoolYearLabel}
                eventsLoading={eventsLoading}
                yearPlannerMonths={yearPlannerMonths}
                canAdd={canAdd}
                startYearPlannerSelection={startYearPlannerSelection}
                updateYearPlannerSelection={updateYearPlannerSelection}
                finishYearPlannerSelection={finishYearPlannerSelection}
                cancelYearPlannerSelection={cancelYearPlannerSelection}
                openCreateForm={openCreateForm}
                isDateInYearPlannerSelection={isDateInYearPlannerSelection}
                eventId={eventId}
                eventScope={eventScope}
                eventClassroomNames={eventClassroomNames}
                classroomLookup={classroomLookup}
                scopeLabels={scopeLabels}
                formatEventTime={formatEventTime}
                canEdit={canEdit}
                openEditForm={openEditForm}
                canRemove={canRemove}
                setDeleteTarget={setDeleteTarget}
              />
            )}
          </>
        )}
      </div>

      <EventFormModal
        t={t}
        formOpen={formOpen}
        setFormOpen={setFormOpen}
        editingEvent={editingEvent}
        form={form}
        setForm={setForm}
        formErrors={formErrors}
        handleSubmit={handleSubmit}
        saving={saving}
        scopeLabels={scopeLabels}
        eventTypeLabels={eventTypeLabels}
        sortedSchoolYears={sortedSchoolYears}
        selectedSchoolYear={selectedSchoolYear}
        yearClassrooms={yearClassrooms}
        entityId={entityId}
        classroomLabel={classroomLabel}
        schoolYearLabel={schoolYearLabel}
        canRemove={canRemove}
        setDeleteTarget={setDeleteTarget}
      />

      <DeleteEventModal
        t={t}
        deleteTarget={deleteTarget}
        setDeleteTarget={setDeleteTarget}
        handleDelete={handleDelete}
      />

      <Modal
        show={confirmDeleteSelected}
        type="confirm"
        title={t("schoolCalendar.deleteSelectedTitle", { defaultValue: "Delete planning items" })}
        message={t("schoolCalendar.deleteSelectedMessage", {
          count: selectedEventIds.length,
          defaultValue: "Are you sure you want to delete the {{count}} selected planning items? This cannot be undone.",
        })}
        onConfirm={deleteSelected}
        onClose={() => setConfirmDeleteSelected(false)}
      />

      <OfficialHolidayModal
        t={t}
        i18n={i18n}
        officialHolidayModalOpen={officialHolidayModalOpen}
        setOfficialHolidayModalOpen={setOfficialHolidayModalOpen}
        selectedSchoolYearApiLabel={selectedSchoolYearApiLabel}
        officialHolidaysError={officialHolidaysError}
        officialHolidaysLoading={officialHolidaysLoading}
        officialHolidays={officialHolidays}
        selectedOfficialHolidayIds={selectedOfficialHolidayIds}
        toggleOfficialHolidaySelection={toggleOfficialHolidaySelection}
        saveOfficialHolidays={saveOfficialHolidays}
        officialHolidaysSaving={officialHolidaysSaving}
        formatDateOnly={formatDateOnly}
      />

      <InfoModal modal={modal} setModal={setModal} />
    </PageTemplate>
  );
};

export default SchoolCalendar;
